import React, { useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { useLifeSpaces, useWindowState, useTabManager, type Tab } from '../hooks/multiWindow'
import { TabBar } from '../components/navigation/TabBar'
import { LifeSpacesSidebar } from '../components/navigation/LifeSpacesSidebar'
import { StandardSidebar } from '../components/navigation/StandardSidebar'
import { RightSidebar } from '../components/navigation/RightSidebar'
import { useTitlebarContext } from '../components/layout/Titlebar'
import { ContentUnionShape } from '../components/layout/ContentUnionShape'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb'
import { Maximize2, Ellipsis } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePlatform } from '../hooks/usePlatform'
import { trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'
import { scheduleSync } from '../lib/sync/sync-engine'
import { AreaIcon } from '../components/icons/AreaIcon'
import { useAutoScrollbar } from '../hooks/useAutoScrollbar'
import { useSetting } from '../lib/db/hooks'
import { db } from '../lib/db/indexeddb'
import { TabManagerProvider } from '../contexts/TabManagerContext'
import { BreadcrumbTitleProvider, useBreadcrumbTitle } from '../contexts/BreadcrumbTitleContext'
import { usePreferencesDialog } from '../contexts/PreferencesDialogContext'
import { UpdateSidebarOverlay } from '../components/layout/UpdateSidebarOverlay'
import './MainLayout.scss'

/** Map an active-tab path to its sidebar section i18n key. */
function getSectionTitle(path: string | undefined, t: (key: string) => string): string {
  if (!path) return ''

  // Quick notes (legacy shared route — keep for backwards compatibility)
  if (path === '/home/quick-notes') return t('sidebar.quickNotes')

  // Personal space
  if (path.startsWith('/home/personal/')) {
    const segment = path.split('/')[3] // today | upcoming | tasks | habits | quick-notes | ...
    if (segment === 'quick-notes') return t('sidebar.quickNotes')
    if (['today', 'upcoming', 'tasks'].includes(segment)) return t('sidebar.quickOverview')
    if (['habits', 'recurring', 'goals'].includes(segment)) return t('sidebar.personalSection')
    return t('sidebar.projects')
  }

  // Work space
  if (path.startsWith('/home/work/')) {
    const segment = path.split('/')[3]
    if (segment === 'quick-notes') return t('sidebar.quickNotes')
    if (['today', 'week', 'upcoming', 'tasks'].includes(segment)) return t('sidebar.workToday')
    if (['habits', 'recurring', 'goals'].includes(segment)) return t('sidebar.workSection')
    return t('sidebar.projects')
  }

  // Home space
  if (path.startsWith('/home/home/')) {
    const segment = path.split('/')[3]
    if (segment === 'quick-notes') return t('sidebar.quickNotes')
    if (['today', 'week', 'upcoming', 'tasks'].includes(segment)) return t('sidebar.homeSection')
    if (['habits', 'recurring', 'goals'].includes(segment)) return t('sidebar.homeSpaceSection')
    return t('sidebar.categories')
  }

  return ''
}

/** Breadcrumb bar that optionally shows a third level (page title) when provided via context. */
const MainBreadcrumb: React.FC<{ spaceName: string; sectionTitle: string }> = ({ spaceName, sectionTitle }) => {
  const { title } = useBreadcrumbTitle()
  return (
    <div className="main-layout__breadcrumb-row">
      <Breadcrumb className="main-layout__breadcrumb">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>{spaceName}</BreadcrumbPage>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className={title ? undefined : 'breadcrumb__page--current'}>{sectionTitle}</BreadcrumbPage>
          </BreadcrumbItem>
          {title && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="breadcrumb__item--animated">
                <BreadcrumbPage className="breadcrumb__page--current">{title}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="main-layout__breadcrumb-actions">
        <button className="main-layout__breadcrumb-action" type="button" onClick={() => { /* TODO */ }}>
          <Maximize2 size={16} />
        </button>
        <button className="main-layout__breadcrumb-action" type="button" onClick={() => { /* TODO */ }}>
          <Ellipsis size={16} />
        </button>
      </div>
    </div>
  )
}

interface MainLayoutProps {
  children: React.ReactNode
}

const MainLayoutComponent: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation()
  const { activeLifeSpace, setActiveLifeSpace, spaces } = useLifeSpaces()

  // Refs for latest values — read in handleLifeSpaceChange to avoid stale closures
  const activeSpaceRef = useRef(activeLifeSpace)
  activeSpaceRef.current = activeLifeSpace
  const spacesRef = useRef(spaces)
  spacesRef.current = spaces
  const { uiState, updateUiState } = useWindowState()
  const { isElectron, isMac } = usePlatform()
  const isWindows = isElectron && !isMac
  const preferencesDialog = usePreferencesDialog()
  const { setValue: setTutorialVisible } = useSetting<boolean>('tutorialProjectVisible', true)
  // `showTopFade` / `showBottomFade` are no longer read — MainLayout's
  // scroll-wrap stopped scrolling when TabStack took over per-slot scrolling,
  // so they never fire. Fades are rendered per TabStack slot now.
  const { containerRef: scrollRef, scrollbarVisible, onScroll: handleContentScroll, flash: flashScrollbar } = useAutoScrollbar()
  const {
    tabs,
    activeTab,
    isSecondaryWindow,
    secondaryReady,
    switchToSpace,
    openTab,
    closeTab,
    setActiveTab,
    pinTab,
    closeOtherTabs,
    reorderTabs,
    navigateActiveTab,
    openPreviewTab,
    promotePreviewTab,
    promotePreviewBySpace,
    updateTab,
    goBack,
    goForward,
    getDefaultTabForLifeSpace
  } = useTabManager()

  // Per-tab scroll state is now preserved automatically by TabStack — each
  // slot is its own scroll container and stays mounted when inactive, so the
  // DOM retains `scrollTop` across switches. The previous save/restore logic
  // keyed off MainLayout's scroll-wrap, which no longer scrolls (content
  // fits exactly) and was always saving/restoring 0.

  // NOTE: Default tab opening is handled in useTabManager hook
  // to avoid conflicts with session restoration

  // Sync project names into ALL open project tabs (not just the active one).
  // Covers cross-device renames arriving via sync.
  // IMPORTANT: Read `tabs` via ref inside the effect to avoid an infinite loop
  // (updateTab changes tabs → would re-trigger the effect if tabs were a dependency).
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs

  // Stable projectTabIds — only change reference when the actual IDs change
  const projectTabIdsRef = useRef<string[]>([])
  const projectTabIds = useMemo(() => {
    const ids: string[] = []
    for (const tab of tabs) {
      const match = tab.path.match(/\/project\/([^/]+)/)
      if (match) ids.push(match[1])
    }
    const prev = projectTabIdsRef.current
    if (ids.length === prev.length && ids.every((id, i) => id === prev[i])) {
      return prev
    }
    projectTabIdsRef.current = ids
    return ids
  }, [tabs])

  const projectData = useLiveQuery(
    () => projectTabIds.length > 0
      ? db.projects.where('id').anyOf(projectTabIds).toArray()
      : Promise.resolve([]),
    [projectTabIds]
  )

  useEffect(() => {
    if (!projectData || projectData.length === 0) return
    const nameById = new Map(projectData.map(p => [p.id, p.name]))
    for (const tab of tabsRef.current) {
      const match = tab.path.match(/\/project\/([^/]+)/)
      if (!match) continue
      const projectName = nameById.get(match[1])
      if (projectName !== undefined && projectName !== tab.title) {
        updateTab(tab.id, { title: projectName || t('project.defaultName') })
      }
    }
  }, [projectData, updateTab, t])

  // Memoized callbacks for sidebar toggles - MUST be defined BEFORE useEffect
  const toggleLeftSidebar = useCallback(() => {
    updateUiState({ sidebarLeft: !uiState.sidebarLeft })
  }, [uiState.sidebarLeft, updateUiState])

  const toggleRightSidebar = useCallback(() => {
    updateUiState({ sidebarRight: !uiState.sidebarRight })
  }, [uiState.sidebarRight, updateUiState])

  // Register sidebar toggles in TitlebarContext so Titlebar buttons can call them
  const { sidebarToggleRef, rightSidebarToggleRef, setSidebarLeftOpen, setSidebarRightOpen, setMainLayoutActive, tabBarSlotRef, tabBarSlotReady, unionShapeSlotRef, unionShapeSlotReady, bumpSlotRef, bumpSlotReady } = useTitlebarContext()

  useEffect(() => {
    sidebarToggleRef.current = toggleLeftSidebar
    rightSidebarToggleRef.current = toggleRightSidebar
    return () => {
      sidebarToggleRef.current = null
      rightSidebarToggleRef.current = null
    }
  }, [toggleLeftSidebar, toggleRightSidebar, sidebarToggleRef, rightSidebarToggleRef])

  // Signal to Titlebar that MainLayout is active (for web route-aware app controls)
  useEffect(() => {
    setMainLayoutActive(true)
    return () => setMainLayoutActive(false)
  }, [setMainLayoutActive])

  // Track app open for retention analytics
  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.APP_OPENED, {
      platform: isElectron ? 'electron' : 'web',
    })
  }, [])

  // Sync sidebar open state to TitlebarContext for icon switching
  useEffect(() => {
    setSidebarLeftOpen(uiState.sidebarLeft)
  }, [uiState.sidebarLeft, setSidebarLeftOpen])

  useEffect(() => {
    setSidebarRightOpen(uiState.sidebarRight)
  }, [uiState.sidebarRight, setSidebarRightOpen])

  // Sort tabs the same way TabBar does (pinned first) for accurate first-tab detection
  const sortedTabs = useMemo(() =>
    tabs.toSorted((a, b) => {
      if (a.isPinned && !b.isPinned) return -1
      if (!a.isPinned && b.isPinned) return 1
      return 0
    }),
    [tabs]
  )

  // Create a new project in the active life space
  const handleCreateProject = useCallback(async () => {
    const lifeSpace = activeLifeSpace as 'personal' | 'work' | 'home'
    if (!['personal', 'work', 'home'].includes(lifeSpace)) return

    const project = await db.createProject({
      name: '',
      description: null,
      color: 'var(--space-500)',
      status: 'active',
      lifeSpace,
      startDate: null,
      endDate: null,
      progress: 0,
    })
    navigateActiveTab({
      path: `/home/${lifeSpace}/project/${project.id}`,
      title: t('project.defaultName'),
      icon: AreaIcon as any,
    })
    scheduleSync()
  }, [activeLifeSpace, navigateActiveTab, t])

  // Refs for volatile values — read inside keydown handler to avoid re-registration
  const activeTabRef = useRef(activeTab)
  activeTabRef.current = activeTab
  const sortedTabsRef = useRef(sortedTabs)
  sortedTabsRef.current = sortedTabs
  const uiStateRef = useRef(uiState)
  uiStateRef.current = uiState

  // Keyboard shortcuts für Sidebar Toggles, Tab Navigation und Quick Add
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey

      // Toggle left sidebar
      if (isMod && e.key === 'b' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        toggleLeftSidebar()
      }

      // Toggle right sidebar
      if (isMod && e.shiftKey && e.key === 'b' && !e.altKey) {
        e.preventDefault()
        toggleRightSidebar()
      }

      // Toggle both sidebars (Cmd+Option+B / Ctrl+Alt+B)
      // Use e.code because Option/Alt changes e.key on macOS (e.g. Option+B → '∫')
      if (isMod && e.altKey && e.code === 'KeyB') {
        e.preventDefault()
        const ui = uiStateRef.current
        const bothOpen = ui.sidebarLeft && ui.sidebarRight
        updateUiState({ sidebarLeft: !bothOpen, sidebarRight: !bothOpen })
      }

      // Skip arrow-key shortcuts when focus is inside a text field
      const target = e.target as HTMLElement
      const isEditing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

      // Tab history back (Cmd+Left / Ctrl+Left)
      if (isMod && !e.altKey && !e.shiftKey && e.code === 'ArrowLeft') {
        if (isEditing) return
        e.preventDefault()
        goBack()
      }

      // Tab history forward (Cmd+Right / Ctrl+Right)
      if (isMod && !e.altKey && !e.shiftKey && e.code === 'ArrowRight') {
        if (isEditing) return
        e.preventDefault()
        goForward()
      }

      // Switch tab left (Cmd+Option+Left / Ctrl+Alt+Left)
      if (isMod && e.altKey && e.code === 'ArrowLeft') {
        if (isEditing) return
        e.preventDefault()
        const st = sortedTabsRef.current
        const at = activeTabRef.current
        if (st.length > 1 && at) {
          const currentIndex = st.findIndex(t => t.id === at.id)
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : st.length - 1
          setActiveTab(st[prevIndex])
        }
      }

      // Switch tab right (Cmd+Option+Right / Ctrl+Alt+Right)
      if (isMod && e.altKey && e.code === 'ArrowRight') {
        if (isEditing) return
        e.preventDefault()
        const st = sortedTabsRef.current
        const at = activeTabRef.current
        if (st.length > 1 && at) {
          const currentIndex = st.findIndex(t => t.id === at.id)
          const nextIndex = currentIndex < st.length - 1 ? currentIndex + 1 : 0
          setActiveTab(st[nextIndex])
        }
      }

      // Life Space cycling (Cmd/Ctrl+Option+Up/Down) is handled in LifeSpacesSidebar
      // alongside the L shortcut — same variables, same callback, guaranteed consistency.


      // Toggle pin on active tab (P, no modifier)
      if (e.key === 'p' && !isMod && !e.altKey && !e.shiftKey) {
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return
        const at = activeTabRef.current
        if (at) {
          e.preventDefault()
          pinTab(at)
        }
      }

      // Close other tabs (Cmd+Shift+W / Ctrl+Shift+W)
      if (isMod && e.shiftKey && e.code === 'KeyW') {
        e.preventDefault()
        const at = activeTabRef.current
        if (at) closeOtherTabs(at)
      }

      // Create new project (Alt+Cmd+N / Alt+Ctrl+N)
      // Use e.code because Option/Alt changes e.key on macOS
      if (isMod && e.altKey && e.code === 'KeyN') {
        e.preventDefault()
        handleCreateProject()
      }

      // Cmd/Ctrl+N is handled by ProjectPage for inline task creation

      // Open preferences (Cmd+, / Ctrl+,)
      // On macOS the OS app menu accelerator (menuService.ts) intercepts this
      // before it reaches the renderer; this branch covers Windows/Linux and
      // the web build, where no native menu is registered.
      if (isMod && !e.altKey && !e.shiftKey && e.code === 'Comma') {
        e.preventDefault()
        if (window.api?.invoke) {
          window.api.invoke('preferences:open')
        } else if (preferencesDialog) {
          preferencesDialog.open()
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [toggleLeftSidebar, toggleRightSidebar, updateUiState, goBack, goForward, setActiveTab, pinTab, closeOtherTabs, handleCreateProject, preferencesDialog])

  // IPC: close active tab (triggered by Cmd+W via main process menu)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('close-active-tab', () => {
      if (!activeTab) return
      if (tabs.length > 1) {
        closeTab(activeTab)
      } else if (isSecondaryWindow && window.api?.invoke) {
        // Last tab in secondary window → close the window
        window.api.invoke('window-close')
      }
    })
    return () => {
      unsubscribe?.()
    }
  }, [activeTab, tabs.length, closeTab, isSecondaryWindow])

  // IPC: create tutorial project (triggered by Help > Tutorial-Projekt erstellen)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('create-tutorial-project', () => {
      setTutorialVisible(true)
      // Switch to personal space and navigate to tutorial
      if (activeLifeSpace !== 'personal') {
        setActiveLifeSpace('personal')
        switchToSpace('personal')
      }
      navigateActiveTab({
        path: '/home/personal/area/Erste-Schritte-in-HappyTask',
        title: t('tutorial.projectName'),
      })
    })
    return () => {
      unsubscribe?.()
    }
  }, [setTutorialVisible, activeLifeSpace, setActiveLifeSpace, switchToSpace, navigateActiveTab, t])

  // IPC: create new project (triggered by File > New Project menu)
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubscribe = window.api.receive('create-new-project', () => {
      handleCreateProject()
    })
    return () => {
      unsubscribe?.()
    }
  }, [handleCreateProject])

  // Secondary window: close when all tabs are gone (e.g. last tab closed via X button)
  // Wait for secondaryReady to avoid closing before init data is pulled
  useEffect(() => {
    if (isSecondaryWindow && secondaryReady && tabs.length === 0 && window.api?.invoke) {
      window.api.invoke('window-close')
    }
  }, [isSecondaryWindow, secondaryReady, tabs.length])


  // Life space change: update active space and switch tabs.
  const handleLifeSpaceChange = useCallback((spaceId: string) => {
    setActiveLifeSpace(spaceId)
    switchToSpace(spaceId)
  }, [setActiveLifeSpace, switchToSpace])

  // Flash content scrollbar briefly on tab change
  useEffect(() => { flashScrollbar() }, [activeTab?.id, flashScrollbar])

  // First tab uses simple corner (no left S-curve) unless on macOS Electron
  // with sidebar collapsed, where the search icon provides space for the S-curve
  const isFirstTabActive = sortedTabs.length > 0 && sortedTabs[0].id === activeTab?.id
    && (isElectron ? (!isMac || uiState.sidebarLeft) : true)

  // Track pinned tab count so ContentUnionShape re-measures when pin order changes
  const pinnedCount = useMemo(() => tabs.filter(t => t.isPinned).length, [tabs])

  // Track max width for TabBar so it never exceeds the content area.
  // Uses direct DOM manipulation (no React state) to avoid re-render jitter
  // during sidebar CSS transitions.
  const contentRef = useRef<HTMLDivElement>(null)
  const tabBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const contentEl = contentRef.current
    const slotEl = tabBarSlotRef.current
    if (!contentEl) return

    const update = () => {
      const barEl = tabBarRef.current
      if (!barEl) return
      if (slotEl) {
        const contentRect = contentEl.getBoundingClientRect()
        const slotRect = slotEl.getBoundingClientRect()
        barEl.style.maxWidth = `${Math.max(0, Math.round(contentRect.right - slotRect.left))}px`
      } else {
        barEl.style.maxWidth = `${contentEl.offsetWidth}px`
      }
    }

    const observer = new ResizeObserver(update)
    observer.observe(contentEl)
    if (slotEl) observer.observe(slotEl)
    update()
    return () => observer.disconnect()
  }, [tabBarSlotRef])

  // Close a tab by its path (used by sidebar close-tab buttons)
  const closeTabByPath = useCallback((path: string) => {
    const tab = tabs.find(t => t.path === path)
    if (tab) closeTab(tab)
  }, [tabs, closeTab])

  // Close the currently active tab (used by page components when their content is deleted)
  const closeActiveTab = useCallback(() => {
    if (!activeTab) return
    if (tabs.length > 1) {
      closeTab(activeTab)
    } else {
      // Last tab — navigate to default page for this life space
      const defaultTab = getDefaultTabForLifeSpace(activeTab.lifeSpace || 'personal')
      if (defaultTab) {
        navigateActiveTab({ path: defaultTab.path, title: defaultTab.title, icon: defaultTab.icon })
      }
    }
  }, [activeTab, tabs.length, closeTab, getDefaultTabForLifeSpace, navigateActiveTab])

  // Sidebar hover → tab highlight: event delegation on sidebar [data-path] elements
  useEffect(() => {
    const sidebar = document.querySelector('.standard-sidebar')
    if (!sidebar) return

    let lastHighlighted: Element | null = null

    const handleOver = (e: Event) => {
      const target = (e.target as HTMLElement).closest('[data-path]')
      const path = target?.getAttribute('data-path')

      if (lastHighlighted) {
        lastHighlighted.classList.remove('tab-bar__tab--sidebar-hover')
        lastHighlighted = null
      }

      if (path) {
        const tabEl = document.querySelector(`.tab-bar__tab[data-path="${CSS.escape(path)}"]:not(.tab-bar__tab--active)`)
        if (tabEl) {
          tabEl.classList.add('tab-bar__tab--sidebar-hover')
          lastHighlighted = tabEl
        }
      }
    }

    const handleOut = (e: Event) => {
      const me = e as MouseEvent
      if (!sidebar.contains(me.relatedTarget as Node)) {
        if (lastHighlighted) {
          lastHighlighted.classList.remove('tab-bar__tab--sidebar-hover')
          lastHighlighted = null
        }
      }
    }

    sidebar.addEventListener('mouseover', handleOver)
    sidebar.addEventListener('mouseout', handleOut)
    return () => {
      sidebar.removeEventListener('mouseover', handleOver)
      sidebar.removeEventListener('mouseout', handleOut)
      if (lastHighlighted) {
        lastHighlighted.classList.remove('tab-bar__tab--sidebar-hover')
      }
    }
  }, [])

  // Tab hover → sidebar highlight: add/remove CSS class on matching sidebar item via DOM
  const lastHoverElRef = useRef<Element | null>(null)
  const handleTabHover = useCallback((path: string | null) => {
    if (lastHoverElRef.current) {
      lastHoverElRef.current.classList.remove('sidebar-item--tab-hover', 'draggable-area-item--tab-hover')
      lastHoverElRef.current = null
    }
    if (path) {
      const el = document.querySelector(`.standard-sidebar [data-path="${CSS.escape(path)}"]`)
      if (el) {
        const cls = el.classList.contains('draggable-area-item') ? 'draggable-area-item--tab-hover' : 'sidebar-item--tab-hover'
        el.classList.add(cls)
        lastHoverElRef.current = el
      }
    }
  }, [])

  // Promote a preview tab to permanent (used by TabBar double-click on preview tab)
  const handlePromotePreview = useCallback((tab: Tab) => {
    promotePreviewTab(tab.id)
  }, [promotePreviewTab])

  // TabBar JSX — rendered via portal into Titlebar slot
  const tabBarElement = (
    <TabBar
      tabs={tabs}
      activeTab={activeTab}
      onTabClick={setActiveTab}
      onTabClose={closeTab}
      onTabPin={pinTab}
      onCloseOthers={closeOtherTabs}
      onReorder={reorderTabs}
      sidebarLeftOpen={uiState.sidebarLeft}
      sidebarRightOpen={uiState.sidebarRight}
      canCloseLastTab={isSecondaryWindow}
      tabBarRef={tabBarRef}
      onTabHover={handleTabHover}
      onPromotePreview={handlePromotePreview}
    />
  )

  return (
    <div className="main-layout">
      {/* TabBar portaled into Titlebar slot */}
      {tabBarSlotReady && tabBarSlotRef.current && createPortal(tabBarElement, tabBarSlotRef.current)}

      {/* ContentUnionShape: bump layers → union-bump-slot (before titlebar in DOM),
          border → union-border-slot (after content in DOM).
          DOM-order stacking ensures labels always paint above bump. */}
      {(bumpSlotReady || unionShapeSlotReady) && (
        <ContentUnionShape
          contentRef={contentRef}
          activeTabId={activeTab?.id}
          isFirstTabActive={isFirstTabActive}
          isPreview={activeTab?.isPreview}
          sidebarLeftOpen={uiState.sidebarLeft}
          sidebarRightOpen={uiState.sidebarRight}
          tabCount={tabs.length}
          pinnedCount={pinnedCount}
          bumpSlotEl={bumpSlotRef.current}
          borderSlotEl={unionShapeSlotRef.current}
        />
      )}

      {/* App Content */}
      <div className="main-layout__body">
        {/* Life Spaces Sidebar - IMMER sichtbar */}
        <LifeSpacesSidebar
          activeSpace={activeLifeSpace}
          onSpaceChange={handleLifeSpaceChange}
        />

        {/* Standard Sidebar - Collapsible */}
        <StandardSidebar
          isOpen={uiState.sidebarLeft}
          onToggle={toggleLeftSidebar}
          lifeSpace={activeLifeSpace}
          activeTab={activeTab}
          tabs={tabs}
          openTab={openTab}
          navigateActiveTab={navigateActiveTab}
          openPreviewTab={openPreviewTab}
          promotePreviewBySpace={promotePreviewBySpace}
          onCloseTabByPath={tabs.length > 1 ? closeTabByPath : undefined}
          onUnpinByPath={(path: string) => {
            const tab = tabs.find(t => t.path === path && t.isPinned)
            if (tab) pinTab(tab)
          }}
          onPinByPath={(path: string) => {
            const tab = tabs.find(t => t.path === path && !t.isPinned)
            if (tab) pinTab(tab)
          }}
        />

        <UpdateSidebarOverlay sidebarIsOpen={uiState.sidebarLeft} />

        {/* Content Area */}
        <div ref={contentRef} className={`main-layout__content${
          !uiState.sidebarRight ? ' main-layout__content--no-right-sidebar' : ''
        }`}>
          <BreadcrumbTitleProvider>
            <MainBreadcrumb
              spaceName={activeLifeSpace === 'all' ? t('lifeSpaces.allBreadcrumb') : spaces.find(s => s.id === activeLifeSpace)?.name ?? ''}
              sectionTitle={getSectionTitle(activeTab?.path, t)}
            />
            <div className="main-layout__scroll-container">
              <div
                ref={scrollRef}
                className={`main-layout__scroll-wrap${scrollbarVisible ? ' main-layout__scroll-wrap--visible' : ''}`}
                onScroll={handleContentScroll}
              >
                <TabManagerProvider
                  tabs={tabs}
                  activeTabId={activeTab?.id ?? null}
                  activeTab={activeTab}
                  updateTab={updateTab}
                  closeActiveTab={closeActiveTab}
                  openTab={openTab}
                >
                  {children}
                </TabManagerProvider>
              </div>
            </div>
          </BreadcrumbTitleProvider>
        </div>

        {/* Right Sidebar with Widgets */}
        <RightSidebar
          isOpen={uiState.sidebarRight}
          onToggle={toggleRightSidebar}
          activeTabPath={activeTab?.path}
          activeLifeSpace={activeLifeSpace as 'personal' | 'work' | 'home' | 'all'}
        />
      </div>
    </div>
  )
}

// Memoize MainLayout to prevent unnecessary re-renders
const MainLayout = React.memo(MainLayoutComponent)

export default MainLayout